import React from 'react'
import styled from '@emotion/styled'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Icon} from '@vio/atlas/Icon'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Toggle} from '@vio/atlas/Toggle'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {useIsSaveFraming} from '../../hooks/useIsSaveFraming'
import {myListMessages} from '../../myListMessages'
import {DesktopIcon} from '../PriceWatchInfoIcon/DesktopIcon'
import {MobileIcon} from '../PriceWatchInfoIcon/MobileIcon'

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s300};
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s400};
  border-top: 1px solid ${cssTheme.colors.border.neutral.secondary};
  position: relative;
`

export const ContextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export const ToggleWrapper = styled.div`
  display: flex;
  flex: 1 auto;
  justify-content: end;
`

export const SubTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
`

// Control keeps its 8px so the experiment is not measuring a spacing change too.
export const MainTextWrapper = styled.div<{hasSaveFraming: boolean}>`
  display: flex;
  align-items: center;
  gap: ${({hasSaveFraming}) =>
    hasSaveFraming
      ? cssTheme.layout.spacing.s250
      : cssTheme.layout.spacing.s300};
`

export const CustomTag = styled(Tag)`
  padding: ${cssTheme.layout.spacing.s100} ${cssTheme.layout.spacing.s250};
  min-height: 20px;
`

interface PriceWatchDetailedToggleProps {
  isToggleOn: boolean
  className?: string
  handleClick: (event: React.SyntheticEvent<Element, Event>) => void
}

export const PriceWatchDetailedToggle = ({
  isToggleOn,
  className,
  handleClick
}: PriceWatchDetailedToggleProps) => {
  const {isMobile} = useDeviceLayout()
  const saveFramingIsOn = useIsSaveFraming()

  return (
    <>
      <Wrapper className={className}>
        <ContextWrapper>
          <MainTextWrapper hasSaveFraming={saveFramingIsOn}>
            {saveFramingIsOn && <Icon name="Heart" size="sm" />}
            <Text variant="labelS">
              {saveFramingIsOn ? (
                <FormattedMessageWrapper {...myListMessages.anchorCardTitle} />
              ) : (
                <FormattedMessageWrapper
                  id="priceWatchDetailedToggle.title"
                  defaultMessage="Setup a price alert"
                />
              )}
            </Text>
            <CustomTag variant="brand" size="sm">
              <FormattedMessageWrapper
                id="priceWatchDesktopBanner.tag"
                defaultMessage="New"
              />
            </CustomTag>
          </MainTextWrapper>

          <SubTextWrapper>
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              {saveFramingIsOn ? (
                <FormattedMessageWrapper
                  {...myListMessages.anchorCardDescription}
                />
              ) : (
                <FormattedMessageWrapper
                  id="priceWatchDetailedToggle.alertMessage"
                  defaultMessage="Alert me when the price changes"
                />
              )}
            </Text>

            {isMobile ? <MobileIcon /> : <DesktopIcon />}
          </SubTextWrapper>
        </ContextWrapper>

        <ToggleWrapper>
          <Toggle
            checked={isToggleOn}
            id="PriceWatchDetailedToggle"
            onChange={handleClick}
            dataId="PriceWatchDetailedToggle"
          />
        </ToggleWrapper>
      </Wrapper>
    </>
  )
}
